import { ApiError, createErrorReporter } from "@bridewell/api-client";
import type { ClientErrorKind } from "@bridewell/shared";
import { t } from "@/i18n";

/**
 * Error reporting for the web app: failures of this page's own code go to the API's error log
 * (`POST /api/client-errors`, see packages/api-client/src/diagnostics.ts). The shared reporter defines the
 * allowlisted diagnostic payload. Never add wedding content or URLs in this adapter.
 */

/** The build this page runs: the commit in CI (vite.config.ts), "dev" locally. */
const RELEASE: string = import.meta.env.VITE_APP_RELEASE || "dev";

let screen: string | undefined;

/** Kept current by main.tsx from the router: the matched route id (`/app/w/$weddingId/guests`), never the path. */
export function setReportScreen(routeId: string | undefined) {
  screen = routeId;
}

const reporter = createErrorReporter({
  platform: "web",
  appVersion: RELEASE,
  route: () => screen,
  context: () => ({
    standalone: typeof matchMedia === "function" && matchMedia("(display-mode: standalone)").matches,
    online: navigator.onLine,
  }),
});

export function reportError(error: unknown, kind: ClientErrorKind) {
  void reporter.report(error, kind);
}

/** Scripts that are not ours: browser extensions, and anything injected into the page from another origin. */
const FOREIGN = /\b(?:chrome|moz|safari(?:-web)?|ms-browser)-extension:\/\//;
const foreign = (stack: unknown) => FOREIGN.test(String(stack ?? ""));

/** Uncaught exceptions and unhandled rejections of this page's own scripts. Call once, before the app renders. */
export function installErrorReporting() {
  const onError = (event: ErrorEvent) => {
    const error: unknown = event.error;
    // A cross-origin script's error arrives as a bare "Script error." without detail or file of ours: nothing to fix here.
    const ours = error instanceof Error || (event.filename ?? "").startsWith(location.origin);
    if (!ours || foreign((error as Error | undefined)?.stack ?? event.filename)) return;
    reportError(error ?? new Error(event.message), "uncaught");
  };
  const onRejection = (event: PromiseRejectionEvent) => {
    if (foreign((event.reason as { stack?: unknown } | null | undefined)?.stack)) return;
    reportError(event.reason, "unhandledRejection");
  };
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  };
}

/** What a person reads when something failed: the API's sentence, plus the request id of a server error to quote to support. */
export function errorMessage(e: unknown): string {
  const text = e instanceof Error ? e.message : String(e);
  return e instanceof ApiError && e.status >= 500 && e.requestId ? `${text} ${t("common.errorReference", { id: e.requestId })}` : text;
}
